// Email preferences / unsubscribe page
// Lets logged-in users turn off or keep on non-essential email notifications

import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext.jsx';
import { useCurrentUserDoc } from '../../../lib/useCurrentUserDoc';
import AuthLayout from '../../../components/auth/AuthLayout';
import { auth } from '../../../../firebaseConfig.js';

const API_PREFERENCES = '/api/email/preferences';

export default function EmailUnsubscribePage({ onNavigate }) {
  const { user } = useAuth();
  const { userDoc, loading: docLoading, refetch } = useCurrentUserDoc();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  const marketingOn = userDoc?.emailPreferences?.marketingAndReminders !== false;

  const savePreference = async (marketingAndReminders) => {
    if (!user) return;
    setError('');
    setSuccess(null);
    setSaving(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(API_PREFERENCES, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ marketingAndReminders }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }
      setSuccess(marketingAndReminders ? 'on' : 'off');
      refetch();
    } catch (err) {
      console.error('[email/preferences]', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <AuthLayout
        title="Manage your email preferences"
        subtitle="Sign in to manage your email notification preferences."
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            You can turn off non-essential InfluenceHub emails (like reminders and product updates) once you’re signed in.
          </p>
          <button
            type="button"
            onClick={() => onNavigate('login')}
            className="w-full rounded-lg bg-[#1B9DA2] px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#168589] focus:outline-none focus:ring-2 focus:ring-[#1B9DA2] focus:ring-offset-2"
          >
            Sign in
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Manage your email preferences"
      subtitle="Control non-essential emails from InfluenceHub."
    >
      <div className="space-y-5">
        <p className="text-sm text-slate-600">
          You can opt out of non-essential InfluenceHub emails, such as reminders and product update emails.
        </p>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-800">
            Turning off email notifications may cause you to miss useful updates and, importantly, potential brand outreach or opportunities sent through InfluenceHub.
          </p>
        </div>

        {docLoading ? (
          <p className="text-sm text-slate-500">Loading your preferences…</p>
        ) : (
          <>
            {success === 'off' && (
              <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                You’ve unsubscribed from non-essential InfluenceHub emails.
              </p>
            )}
            {success === 'on' && (
              <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                Your email notifications are still on.
              </p>
            )}
            {error && (
              <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {error}
              </p>
            )}

            <p className="text-xs text-slate-500">
              Current setting: {marketingOn ? 'Notifications on' : 'Notifications off'}
            </p>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                disabled={saving}
                onClick={() => savePreference(false)}
                className="w-full rounded-lg bg-[#1B9DA2] px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#168589] disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#1B9DA2] focus:ring-offset-2"
              >
                {saving ? 'Saving…' : 'Turn off email notifications'}
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => savePreference(true)}
                className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
              >
                Keep notifications on
              </button>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
